import React, { useState } from 'react';
import { Button } from 'src/components/ui/button';
import { useParams, useNavigate } from 'react-router-dom';
import { Breadcrumb } from 'src/components/common/Breadcrumb';
import { Alert } from 'src/components/common/Alert';
import { StatusBadge } from 'src/components/common/StatusBadge';
import { Download } from 'lucide-react';
import { useToast } from 'src/hooks/use-toast';
import { useCreditNoteInfo } from 'src/services/api/queries/billingQueries';
import { useApproveCreditNote } from 'src/services/api/mutations/billingMutations';
import { Textarea } from 'src/components/ui/textarea';
import { PDFDownloadLink } from '@react-pdf/renderer';
import CreditNotesPDF from 'src/components/CreditNotesPDF/CreditNotesPDF';
import { BILLING_STATUS_VALUES } from '../../../utils/statusConstants';

const transformApiResponse = (apiData) => {
  // Add defensive checks for apiData
  if (!apiData) {
    throw new Error('No API data received');
  }

  // Safely destructure with default values
  const {
    header = {},
    candidate_details: candidateDetails = {},
    credit_note_details: creditNoteDetails = {},
    meta = {},
  } = apiData;
  // Add defensive checks for required nested objects
  if (!header || !creditNoteDetails || !meta) {
    throw new Error('Invalid API response structure');
  }

  return {
    candidateDetails,
    id: meta.id || 'N/A',
    creditNoteNumber: header.credit_note_number || 'N/A',
    creditNoteDate: header.issued_date || 'N/A',
    poNumber: header.po_number || 'N/A',
    poId: meta.po_id || 'N/A',
    client: {
      name: header.customer?.name || 'N/A',
      id: header.customer?.id || 'N/A',
      gstin: header.customer?.gstin || 'N/A',
      address: {
        line1: header.customer?.address_line_1 || '',
        street: header.customer?.street || '',
        zipCode: header.customer?.zip_code || '',
      },
    },
    ahedge: {
      name: header.ahedge?.name || 'N/A',
      address: header.ahedge?.address || 'N/A',
      cityStatePin: header.ahedge?.city_state_pin || 'N/A',
      gstin: header.ahedge?.gstin || 'N/A',
    },
    creditNoteDetails: {
      creditNoteType: creditNoteDetails.credit_note_type || 'N/A',
      subTotal: creditNoteDetails.amount_before_tax || 0,
      taxAmount: creditNoteDetails.tax_amount || 0,
      taxPercentage: creditNoteDetails.tax_percentage || 0,
      totalAmount: creditNoteDetails.total_amount || 0,
      currency: creditNoteDetails.currency || 'INR',
      totalPaid: creditNoteDetails.total_paid || 0,
      balanceDue: creditNoteDetails.balance_due || 0,
      requirementId: meta.requirement_id || '',
      requirementRegId: meta.registration_id || '',
    },
    meta: {
      id: meta.id || 'N/A',
      status: creditNoteDetails.status || 'N/A',
      customerCategory: meta.customer_category || 'N/A',
      milestoneType: meta.milestone_type || 'N/A',
      createdAt: meta.created_at || 'N/A',
      updatedAt: meta.updated_at || 'N/A',
    },
  };
};

const InfoCard = ({ title, children, className = '' }) => (
  <div className={`bg-card rounded-md shadow-sm p-4 ${className}`}>
    <h3 className="text-sm font-semibold text-gray-600 mb-2">{title}</h3>
    {children}
  </div>
);

const StatusTag = ({ status }) => (
  <StatusBadge value={status} label={BILLING_STATUS_VALUES[status]} />
);

const getBreadcrumbItems = (id) => [
  { label: 'Home', href: '/' },
  { label: 'Credit Notes', href: '/credit-notes' },
  { label: 'Credit Note Details', href: `/credit-notes/${id}` },
];

const CreditNotesInfo = () => {
  const { id } = useParams();
  const { data: response, isLoading, error } = useCreditNoteInfo(id);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showVerifyAlert, setShowVerifyAlert] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState('');
  const approveCreditNoteMutation = useApproveCreditNote();
  let creditNoteData = null;
  let transformationError = null;

  if (response?.success) {
    try {
      // The response has success and data properties
      creditNoteData = transformApiResponse(response.data);
    } catch (err) {
      transformationError = err.message;
      console.error('Error transforming credit note data:', err);
    }
  }

  if (isLoading) {
    return <div>Loading credit note details...</div>;
  }

  if (error) {
    return <Alert variant="destructive">{error.message}</Alert>;
  }

  if (response && !response.success) {
    return (
      <Alert variant="destructive">
        {response.message || 'Failed to load credit note details'}
      </Alert>
    );
  }

  if (transformationError) {
    return (
      <Alert variant="destructive">
        Error processing data: {transformationError}
      </Alert>
    );
  }

  if (!creditNoteData) {
    return <div>No credit note data found.</div>;
  }

  const handleVerifyCreditNote = async () => {
    try {
      const payload = {
        approval_notes: approvalNotes.trim(),
      };

      const result = await approveCreditNoteMutation.mutateAsync({
        id,
        data: payload,
      });
      if (result?.success) {
        toast({
          title: 'Success',
          description: 'Credit note has been approved successfully',
        });
        setShowVerifyAlert(false);
        setApprovalNotes(''); // Reset notes after successful approval
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to approve credit note',
        variant: 'destructive',
      });
    }
  };

  const handleCloseAlert = () => {
    setShowVerifyAlert(false);
    setApprovalNotes(''); // Reset notes when closing
  };

  return (
    <div className="text-left space-y-4">
      {/* Header with actions */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold md:text-2xl pageTitle">
            Credit Note Details
          </h1>
        </div>

        <div className="flex gap-2">
          {/* Download PDF button - always visible for credit notes */}
          <PDFDownloadLink
            document={<CreditNotesPDF data={creditNoteData} />}
            fileName={`CreditNote-${creditNoteData.creditNoteNumber}-${creditNoteData.creditNoteDate}.pdf`}
          >
            {({ blob, loading, error: pdfError }) => (
              <Button
                className="bg-primary hover:bg-brand-deep text-white font-medium shadow-sm"
                disabled={loading}
                onClick={() => {
                  if (pdfError) {
                    toast({
                      title: 'Error',
                      description: 'Failed to generate PDF. Please try again.',
                      variant: 'destructive',
                    });
                  } else if (blob && !loading) {
                    toast({
                      title: 'Success',
                      description: 'PDF generated successfully!',
                    });
                  }
                }}
              >
                <Download className="h-4 w-4 mr-2" />
                {loading ? 'Generating PDF...' : 'Download PDF'}
              </Button>
            )}
          </PDFDownloadLink>

          {/* {creditNoteData.meta.status === 'DRAFT' && (
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={() => setShowVerifyAlert(true)}
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Approve Credit Note
            </Button>
          )} */}
        </div>
      </div>
      <Breadcrumb items={getBreadcrumbItems(creditNoteData.id)} />
      <div className="flex flex-col md:flex-row gap-6 mt-4">
        <div className="flex-1 space-y-4">
          <div className="lightBlueBg p-4 rounded-md mb-4 flex flex-wrap items-center gap-4">
            <div>
              <span className="font-medium text-sm text-gray-700">
                Credit Note Number:
              </span>
              <span className="ml-1 text-sm font-bold">
                {creditNoteData.creditNoteNumber}
              </span>
            </div>

            <div className="text-gray-400">•</div>

            <div>
              <span className="font-medium text-sm text-gray-700">
                Issued Date:
              </span>
              <span className="ml-1 text-sm font-bold">
                {creditNoteData.creditNoteDate}
              </span>
            </div>

            <div className="text-gray-400">•</div>

            <div className="flex items-center">
              <span className="font-medium text-sm text-gray-700 mr-2">
                Status:
              </span>
              <StatusTag status={creditNoteData.meta.status} />
            </div>
          </div>
          <div className="lightBlueBg p-6 rounded-md mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <InfoCard title="From">
                <h4 className="font-medium text-base">
                  {creditNoteData.ahedge.name}
                </h4>
                <p className="text-sm text-gray-600 mt-1">
                  {creditNoteData.ahedge.address},{' '}
                  {creditNoteData.ahedge.cityStatePin}
                </p>
                <p className="text-sm mt-2">
                  <span className="font-medium">GSTIN:</span>{' '}
                  {creditNoteData.ahedge.gstin}
                </p>
              </InfoCard>
              <InfoCard title="To">
                <h4 className="font-medium text-base">
                  {creditNoteData.client.name}
                </h4>
                <p className="text-sm text-gray-600 mt-1">
                  {creditNoteData.client.address.line1},{' '}
                  {creditNoteData.client.address.street},{' '}
                  {creditNoteData.client.address.zipCode}
                </p>
                <p className="text-sm mt-2">
                  <span className="font-medium">GSTIN:</span>{' '}
                  {creditNoteData.client.gstin}
                </p>
              </InfoCard>
            </div>
            <InfoCard title="References" className="mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm">
                    <span className="font-medium">PO Number:</span>{' '}
                    <a
                      href={`/purchase-order/${creditNoteData.poId}`}
                      className="text-primary hover:underline"
                    >
                      {creditNoteData.poNumber}
                    </a>
                  </p>
                </div>
                <div>
                  <p className="text-sm">
                    <span className="font-medium">Requirement Id:</span>{' '}
                    <a
                      href={`/opportunity/${creditNoteData.creditNoteDetails.requirementId}`}
                      className="text-primary hover:underline"
                    >
                      {creditNoteData.creditNoteDetails.requirementRegId}
                    </a>
                  </p>
                </div>
              </div>
            </InfoCard>
            <InfoCard title="Candidate Details" className="mb-6">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-primary text-white">
                      <th className="p-2 text-left">Candidate ID</th>
                      <th className="p-2 text-left">Candidate Name</th>
                      <th className="p-2 text-left">Requirement</th>
                      <th className="p-2 text-left">Joining Date</th>
                      <th className="p-2 text-right">CTC Amount</th>
                      <th className="p-2 text-right">Cost Per Candidate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {response.data.candidate_details ? (
                      <tr>
                        <td className="p-3">
                          {response.data.candidate_details.candidate_id}
                        </td>
                        <td className="p-3">
                          {response.data.candidate_details.candidate_name}
                        </td>
                        <td className="p-3">
                          {response.data.meta.requirement_title}
                        </td>
                        <td className="p-3">
                          {response.data.candidate_details.joining_date}
                        </td>
                        <td className="p-3 text-right">
                          ₹
                          {response.data.candidate_details.ctc_amount?.toLocaleString()}
                        </td>
                        <td className="p-3 text-right">
                          ₹
                          {response.data.candidate_details.cost_per_candidate?.toLocaleString()}
                        </td>
                      </tr>
                    ) : (
                      <tr>
                        <td
                          colSpan="6"
                          className="p-3 text-center text-gray-500"
                        >
                          No candidate details found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </InfoCard>

            <InfoCard title="Summary">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-primary text-white">
                      <th className="p-2 text-left">Invoice Number</th>
                      <th className="p-2 text-left">Due Date</th>
                      <th className="p-2 text-left">Milestone</th>
                      <th className="p-2 text-left">Milestone Date</th>
                      <th className="p-2 text-right">Invoice Total</th>
                      <th className="p-2 text-right">Total Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {response.data.invoice_details &&
                    response.data.invoice_details.length > 0 ? (
                      response.data.invoice_details.map((item, idx) => (
                        <tr
                          key={item.invoice_item_id || idx}
                          className="border-b"
                        >
                          <td className="p-3">{item.invoice_number}</td>
                          <td className="p-3">{item.due_date}</td>
                          <td className="p-3">{item.milestone_text}</td>
                          <td className="p-3"> {item.milestone_date}</td>
                          <td className="p-3 text-right">
                            ₹{item.invoice_total?.toLocaleString()}
                          </td>
                          <td className="p-3 text-right">
                            ₹{item.item_amount?.toLocaleString()}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="6"
                          className="p-3 text-center text-gray-500"
                        >
                          No invoice details found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {/* Sub Total below the table, right aligned */}
              {response.data.invoice_details &&
                response.data.invoice_details.length > 0 && (
                  <div className="flex justify-end mt-2 p-3 flex-col text-sm">
                    <div className="border-b flex justify-end">
                      <div className="p-3 text-left font-medium">Sub Total</div>
                      <div className="p-3 text-right w-auto">
                        ₹{' '}
                        {response.data.credit_note_details.amount.toLocaleString()}
                      </div>
                    </div>
                    <div className="border-b flex justify-end">
                      <div className="p-3 text-left">GST</div>
                      <div className="p-3 text-right">
                        ₹{' '}
                        {response.data.credit_note_details.gst_amount.toLocaleString()}
                      </div>
                    </div>
                    <div className="border-b flex justify-end bg-brand-bg-subtle font-semibold">
                      <div className="p-3 text-left">Total</div>
                      <div className="p-3 text-right">
                        ₹{' '}
                        {response.data.credit_note_details.total_amount.toLocaleString()}
                      </div>
                    </div>
                  </div>
                )}
            </InfoCard>

            <div className="flex justify-end mt-6">
              <Button
                variant="outline"
                onClick={() => navigate('/credit-note')}
              >
                Back to Credit Note
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Verification Alert */}
      <Alert
        isOpen={showVerifyAlert}
        onClose={handleCloseAlert}
        title="Approve Credit Note"
        description={`Are you sure you want to approve credit note ${creditNoteData.creditNoteNumber}?`}
        cancelText="Cancel"
        continueText={
          approveCreditNoteMutation.isPending ? 'Processing...' : 'Approve'
        }
        onCancel={handleCloseAlert}
        onContinue={handleVerifyCreditNote}
        customContent={
          <div className="mt-4">
            <div className="block text-sm font-medium text-gray-700 mb-2">
              Approval Notes (optional)
            </div>
            <Textarea
              id="approval-notes-textarea"
              name="approval-notes"
              placeholder="Enter approval notes..."
              value={approvalNotes}
              onChange={(e) => setApprovalNotes(e.target.value)}
              maxLength={50}
              className="w-full"
              rows={3}
              aria-label="Approval Notes (optional)"
              aria-describedby="approval-notes-counter"
            />
            <div
              id="approval-notes-counter"
              className="text-xs text-gray-500 mt-1"
            >
              {approvalNotes.length}/50 characters
            </div>
          </div>
        }
      />
    </div>
  );
};

export default CreditNotesInfo;
